import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'

import { Pencil, Plus } from '@/components/icons'
import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellContent,
	CellText,
	CellTextDate,
	CellTextHeader,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'

import { useAuth } from '@/libs/hooks/use-auth'

import { productCategoryList } from '@/features/product/api'
import type { ICategory } from '@/features/product/type'

export const Route = createFileRoute('/_app/product/category/')({
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout>
			<PageLayout.Header
				breadcrumbs={[
					['/product', 'Produk'],
					[Route.fullPath, 'Kategori'],
				]}
			/>
			<PageCard>
				<PageCard.Header
					title="Daftar Kategori Produk"
					actions={
						<PageCard.HeaderActionRoot>
							<PageCard.HeaderAction asChild>
								<Link from={Route.fullPath} to="/product/category/create">
									<Plus />
									Tambah kategori
								</Link>
							</PageCard.HeaderAction>
						</PageCard.HeaderActionRoot>
					}
				/>
				<CategoryTable />
			</PageCard>
		</PageLayout>
	)
}

function CategoryTable() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(productCategoryList.qry({ outletId }))
	const columns = useMemo(getColumns, [])
	const { table } = useDataTable({
		columns,
		data: data?.categories ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const ch = createColumnHelper<ICategory>()

function getColumns() {
	return [
		ch.accessor('name', {
			header: 'Nama Kategori',
			cell: ({ row }) => <CellText text={row.original.name} />,
		}),
		ch.accessor('isActive', {
			header: 'Aktif',
			cell: ({ row }) => (
				<CellText text={row.original.isActive ? 'Yes' : 'No'} />
			),
		}),
		ch.accessor('createdAt', {
			header: 'Created At',
			cell: ({ row }) => <CellTextDate date={row.original.createdAt} />,
		}),
		ch.accessor('updatedAt', {
			header: 'Updated At',
			cell: ({ row }) => <CellTextDate date={row.original.updatedAt} />,
		}),
		ch.display({
			id: 'actions',
			header: () => <CellTextHeader text="Aksi" align="center" />,
			cell: ({ row }) => {
				return (
					<CellContent className="flex items-center justify-center gap-2">
						<Link
							from={Route.fullPath}
							to={`/product/category/${row.original.id}/update`}
							params={{ id: row.original.id }}
						>
							<Pencil />
						</Link>
					</CellContent>
				)
			},
		}),
	]
}
